// ─── RouteProcessor — GPS filtering, smoothing, elevation, distance ───────────
import type { GpsPoint, ProcessedPoint, RouteQuality } from "./types";
import { haversine } from "./LocationService";

const MAX_POINT_ACCURACY = 200;
const MAX_SPEED_MPS = 12.0;
const ELEVATION_SMOOTH_WINDOW = 3;
const MIN_ELEVATION_CHANGE = 2;

export class RouteProcessor {
  private points: ProcessedPoint[] = [];
  private seq = 0;
  private totalDistance = 0;
  private elevationGain = 0;
  private elevationLoss = 0;
  private elevationHistory: number[] = [];
  private minElevation: number | null = null;
  private maxElevation: number | null = null;
  private lastValid: GpsPoint | null = null;
  private lastGoodForDistance: GpsPoint | null = null;
  private hadPoorGap = false;

  reset() {
    this.points = [];
    this.seq = 0;
    this.totalDistance = 0;
    this.elevationGain = 0;
    this.elevationLoss = 0;
    this.elevationHistory = [];
    this.minElevation = null;
    this.maxElevation = null;
    this.lastValid = null;
    this.lastGoodForDistance = null;
    this.hadPoorGap = false;
  }

  addPoint(raw: GpsPoint): ProcessedPoint | null {
    // Accept first point unconditionally as origin
    if (!this.lastValid) {
      this.lastValid = raw;
      this.points.push({
        ...raw,
        seq: this.seq++,
        distanceFromStart: 0,
        elevationDelta: 0,
      });
      return this.points[this.points.length - 1];
    }

    // Reject extremely poor fixes entirely
    if (raw.accuracy > MAX_POINT_ACCURACY) return null;

    const dt = (raw.ts - this.lastValid.ts) / 1000;
    if (dt <= 0) return null;

    const dist = haversine(this.lastValid.lat, this.lastValid.lng, raw.lat, raw.lng);
    const calcSpeed = dist / dt;

    // Reject absurd speeds (> 12 m/s ≈ 43 km/h)
    if (calcSpeed > MAX_SPEED_MPS) return null;

    // Skip duplicate points (same location, very close in time)
    if (dist < 0.3 && dt < 3) return null;

    // Only accumulate distance between consecutive good fixes (accuracy ≤ 50m).
    // Poor fixes (>50m) are stored as diagnostic points but don't contribute
    // to distance. Gaps caused by poor fixes are not bridged.
    if (raw.moving && dist > 0.3 && raw.accuracy <= 50 && !this.hadPoorGap && this.lastGoodForDistance) {
      this.totalDistance += dist;
    }
    if (raw.accuracy > 50) {
      this.hadPoorGap = true;
    } else {
      this.lastGoodForDistance = raw;
      this.hadPoorGap = false;
    }

    let elevDelta = 0;
    if (raw.altitude !== null) {
      const smoothed = this.smoothElevation(raw.altitude);
      if (this.minElevation === null || smoothed < this.minElevation) this.minElevation = smoothed;
      if (this.maxElevation === null || smoothed > this.maxElevation) this.maxElevation = smoothed;
      if (this.lastValid.altitude !== null) {
        const diff = smoothed - this.smoothElevation(this.lastValid.altitude);
        if (Math.abs(diff) >= MIN_ELEVATION_CHANGE) {
          if (diff > 0) this.elevationGain += diff;
          else this.elevationLoss += Math.abs(diff);
          elevDelta = diff;
        }
      }
    }

    const processed: ProcessedPoint = {
      ...raw,
      seq: this.seq++,
      distanceFromStart: this.totalDistance,
      elevationDelta: elevDelta,
    };

    this.points.push(processed);
    this.lastValid = raw;
    return processed;
  }

  private smoothElevation(alt: number): number {
    this.elevationHistory.push(alt);
    if (this.elevationHistory.length > ELEVATION_SMOOTH_WINDOW) this.elevationHistory.shift();
    return this.elevationHistory.reduce((a, b) => a + b, 0) / this.elevationHistory.length;
  }

  getPoints(): ProcessedPoint[] { return this.points; }
  getDistance(): number { return this.totalDistance; }
  getElevationGain(): number { return this.elevationGain; }
  getElevationLoss(): number { return this.elevationLoss; }
  getMinElevation(): number | null { return this.minElevation; }
  getMaxElevation(): number | null { return this.maxElevation; }

  getRouteQuality(): RouteQuality {
    if (this.points.length === 0) return "unknown";
    const avgAcc = this.points.reduce((s, p) => s + p.accuracy, 0) / this.points.length;
    if (avgAcc <= 10) return "high";
    if (avgAcc <= 25) return "moderate";
    return "low";
  }

  getAvgAccuracy(): number {
    if (this.points.length === 0) return 0;
    return this.points.reduce((s, p) => s + p.accuracy, 0) / this.points.length;
  }

  compressRoute(maxPoints: number = 500): GpsPoint[] {
    if (this.points.length <= maxPoints) return this.points;
    const step = Math.ceil(this.points.length / maxPoints);
    const compressed: GpsPoint[] = [];
    for (let i = 0; i < this.points.length; i += step) {
      compressed.push({
        lat: this.points[i].lat,
        lng: this.points[i].lng,
        ts: this.points[i].ts,
        accuracy: this.points[i].accuracy,
        speed: this.points[i].speed,
        heading: this.points[i].heading,
        altitude: this.points[i].altitude,
        moving: this.points[i].moving,
      });
    }
    if (this.points.length > 0) {
      const last = this.points[this.points.length - 1];
      if (compressed[compressed.length - 1].ts !== last.ts) {
        compressed.push({
          lat: last.lat, lng: last.lng, ts: last.ts,
          accuracy: last.accuracy, speed: last.speed, heading: last.heading,
          altitude: last.altitude, moving: last.moving,
        });
      }
    }
    return compressed;
  }
}
